// src/pages/LoginPage.tsx (Updated with OTP support)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, LogIn, Sparkles, Key, Send, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/TranslationProvider';
import { AuthLayout } from '@/layouts/AuthLayout/AuthLayout';
import { Input } from '@/layouts/AuthLayout/ui/Input';
import { Button } from '@/layouts/AuthLayout/ui/Button';
import { Divider } from '@/layouts/AuthLayout/ui/Divider';
import { Alert } from '@/layouts/AuthLayout/ui/Alert';
import { SocialButton } from '@/layouts/AuthLayout/ui/SocialButton';
import PassKeyLogin from './PassKeyLogin';

type LoginMode = 'password' | 'otp';

const LoginPage: React.FC = () => {
  const { t } = useLang();
  const { login, loginWithOtp, requestOtp, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<LoginMode>('password');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otpCode: '',
  });
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [otpExpiry, setOtpExpiry] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  // Password Login
  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate(location?.state?.from || "/home");
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || t("invalid_credentials") || "Invalid login.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Request OTP
  const handleRequestOtp = async () => {
    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const sessionId = await requestOtp(formData.email);
      setOtpSessionId(sessionId);
      setOtpExpiry(600); // 10 minutes expiry
      setSuccess("Verification code sent to your email!");
      setResendTimer(60); // 60 seconds cooldown
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to send login code";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and complete login
  const handleVerifyOtp = async () => {
    if (!formData.otpCode) {
      setError("Please enter the verification code");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await loginWithOtp(formData.email, formData.otpCode, otpSessionId || undefined);
      navigate(location?.state?.from || "/home");
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Invalid verification code";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const sessionId = await requestOtp(formData.email);
      setOtpSessionId(sessionId);
      setSuccess("New verification code sent to your email!");
      setResendTimer(60);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to resend code";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Reset OTP flow
  const handleBackToPassword = () => {
    setMode('password');
    setOtpSessionId(null);
    setOtpExpiry(null);
    setFormData({ ...formData, otpCode: '' });
    setError(null);
    setSuccess(null);
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err: any) {
      setError(err.message || "Google login failed");
      setLoading(false);
    }
  };

  // Format time remaining
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePasskeySuccess = (token: string) => {
  localStorage.setItem("token", token);
  navigate(location?.state?.from || "/home");
};

  return (
    <AuthLayout 
      title={mode === 'password' ? t("auth.sign_in") : "One-Time Code Login"}
      subtitle={
        mode === 'password' 
          ? "Welcome back! Please enter your details"
          : otpSessionId 
            ? "Enter the verification code sent to your email"
            : "Enter your email to receive a login code"
      }
    >
      {error && (
        <Alert 
          type="error" 
          message={error} 
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert 
          type="success" 
          message={success} 
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Mode Toggle - Only show when not in OTP verification mode */}
      {!otpSessionId && (
        <div className="flex gap-2 mb-6 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <button
            type="button"
            onClick={() => setMode('password')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
              mode === 'password'
                ? 'bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Password
          </button>
          <button
            type="button"
            onClick={() => setMode('otp')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
              mode === 'otp'
                ? 'bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <Key className="w-4 h-4 inline mr-2" />
            One-Time Code
          </button>
        </div>
      )}

      {/* Password Login Form */}
      {mode === 'password' && !otpSessionId && (
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <Input
            type="email"
            name="email"
            label={t("email")}
            icon={<Mail className="w-4 h-4" />}
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
          />

          <Input
            type="password"
            name="password"
            label={t("password")}
            icon={<Lock className="w-4 h-4" />}
            required
            value={formData.password}
            onChange={handleChange}
            placeholder="••••••••"
          />

          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm text-purple-600 dark:text-purple-400 hover:underline transition"
            >
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            loading={loading}
            icon={<LogIn className="w-5 h-5" />}
            fullWidth
          >
            {t("login")}
          </Button>
        </form>
      )}

      {/* OTP Login - Step 1: Request Code */}
      {mode === 'otp' && !otpSessionId && (
        <div className="space-y-4">
          <Input
            type="email"
            name="email"
            label="Email Address"
            icon={<Mail className="w-4 h-4" />}
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
          />

          <Button
            type="button"
            onClick={handleRequestOtp}
            loading={loading}
            icon={<Send className="w-5 h-5" />}
            fullWidth
          >
            Send Login Code
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setMode('password')}
              className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
            >
              ← Back to password login
            </button>
          </div>
        </div>
      )}

      {/* OTP Login - Step 2: Verify Code */}
      {otpSessionId && (
        <div className="space-y-4">
          <div className="text-center mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              We sent a verification code to <strong className="text-purple-600">{formData.email}</strong>
            </p>
            {otpExpiry && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Code expires in {formatTime(otpExpiry)}
              </p>
            )}
          </div>

          <Input
            type="text"
            name="otpCode"
            label="Verification Code"
            icon={<Key className="w-4 h-4" />}
            required
            value={formData.otpCode}
            onChange={handleChange}
            placeholder="000000"
            maxLength={6}
            autoFocus
          />

          <Button
            type="button"
            onClick={handleVerifyOtp}
            loading={loading}
            icon={<LogIn className="w-5 h-5" />}
            fullWidth
          >
            Verify & Sign In
          </Button>

          <div className="flex justify-between items-center text-sm">
            <button
              type="button"
              onClick={handleBackToPassword}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendTimer > 0}
              className={`text-purple-600 dark:text-purple-400 hover:underline ${
                resendTimer > 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {resendTimer > 0 ? `Resend in ${formatTime(resendTimer)}` : "Resend Code"}
            </button>
          </div>
        </div>
      )}

      {/* Divider and Social Login - Only show in password mode and not in OTP flow */}
      {mode === 'password' && !otpSessionId && (
        <>
    
 <Divider text={t("or")} />


    <PassKeyLogin
  email={formData.email}
  onSuccess={handlePasskeySuccess}
  onError={(msg) => setError(msg)}
  disabled={loading}
/>

          <Divider text={t("or")} />

          <SocialButton
            provider="google"
            onClick={handleGoogleLogin}
            loading={loading}
          />

          <div className="mt-6 text-center">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Don't have an account?{" "}
              <Link
                to="/Register"
                className="text-purple-600 dark:text-purple-400 font-semibold hover:underline inline-flex items-center gap-1"
              >
                Sign up
                <Sparkles className="w-3 h-3" />
              </Link>
            </p>
          </div>
        </>
      )}

      {/* Quick tip for OTP mode */}
      {mode === 'otp' && !otpSessionId && (
        <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-xs text-blue-600 dark:text-blue-400 text-center">
            ✨ No password needed! We'll send a one-time code to your email.
          </p>
        </div>
      )}
    </AuthLayout>
  );
};

export default LoginPage;